import {
    CountryCode,
    ProductState,
    VendorSource,
    VendorSupportDto,
    VendorType,
    VendorCategorySupportDto
} from '@api/WalletClient';
import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { PageHeader } from '@components/page-header';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Filters } from '../../components/filters';
import { CountrySelect } from '../../components/country-select';
import { userSearchDebounce } from '../../components/hooks/use-search';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { CellProps, TableComponent } from '../../components/table';
import { getCdnImage } from '../../components/utils/cdn-image';
import { Api } from '../../core/api';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import { StateCell } from '../components/state-cell';

type StateFilterType = 'ENABLED_FOR_USER' | 'POSSIBLE_TO_ENABLE' | 'ALL';
type VendorSourceFilterType = VendorSource | 'ALL';
type CountryCodeFilterType = CountryCode | 'ALL';
type VendorTypeFilterType = VendorType | 'ALL';
type CategoryFilterType = string;

const FILTERS_STORAGE_KEY = 'vendor-list-filters';

interface SavedFilters {
    stateFilter: StateFilterType;
    vendorSourceFilter: VendorSourceFilterType;
    countriesFilter: CountryCodeFilterType;
    vendorTypeFilter: VendorTypeFilterType;
    categoryFilter: string;
}

const loadFiltersFromStorage = (): Partial<SavedFilters> => {
    try {
        const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    } catch (error) {
        console.error('Failed to load filters from localStorage:', error);
        return {};
    }
};

const saveFiltersToStorage = (filters: SavedFilters) => {
    try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
        console.error('Failed to save filters to localStorage:', error);
    }
};

export const VendorList = observer(() => {
    const { walletClient } = injectTSDI(Api);
    const navigate = useNavigate();

    const savedFilters = loadFiltersFromStorage();

    const [stateFilter, setStatusFilter] = React.useState<StateFilterType>(
        savedFilters.stateFilter || 'ALL'
    );
    const [vendorSourceFilter, setVendorSourceFilter] =
        React.useState<VendorSourceFilterType>(
            savedFilters.vendorSourceFilter || 'ALL'
        );
    const [countriesFilter, setCountriesFilter] =
        React.useState<CountryCodeFilterType>(
            savedFilters.countriesFilter || 'ALL'
        );
    const [vendorTypeFilter, setVendorTypeFilter] =
        React.useState<VendorTypeFilterType>(
            savedFilters.vendorTypeFilter || 'ALL'
        );
    const [categoryFilter, setCategoryFilter] = React.useState<string>(
        savedFilters.categoryFilter || 'ALL'
    );
    const [availableCategories, setAvailableCategories] = React.useState<
        VendorCategorySupportDto[]
    >([]);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: 'name'
    });

    const { setSearchTerm } = userSearchDebounce(dispatch);

    React.useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response =
                    await walletClient.supportApi.vendorCategorySupportControllerGetAllCategories();
                const activeCategories = (response.data || [])
                    .filter((c) => !c.deletedDate)
                    .sort((a, b) => a.displayOrder - b.displayOrder);
                setAvailableCategories(activeCategories);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, []);

    const vendors = useWrapRequest(
        async (
            searchParams,
            stateFilter,
            vendorSourceFilter,
            countriesFilter,
            vendorTypeFilter,
            categoryFilter
        ) => {
            const result =
                await walletClient.supportApi.vendorSupportControllerGetPage({
                    ...searchParams,
                    ...(stateFilter === 'ENABLED_FOR_USER' && {
                        shopStates: [ProductState.PRODUCTION]
                    }),
                    ...(stateFilter === 'POSSIBLE_TO_ENABLE' && {
                        internalStates: [ProductState.DISABLED],
                        internalStatesIncludeNull: true,
                        externalStates: ProductState.PRODUCTION
                    }),
                    ...(vendorSourceFilter !== 'ALL' && {
                        sources: vendorSourceFilter
                    }),
                    ...(countriesFilter !== 'ALL' && {
                        country: countriesFilter
                    }),
                    ...(vendorTypeFilter !== 'ALL' && {
                        vendorType: vendorTypeFilter
                    }),
                    ...(categoryFilter !== 'ALL' && {
                        categoryIds: [Number(categoryFilter)]
                    })
                });

            return { ...result.data, content: result.data.content || [] };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 10,
                totalElements: 0,
                totalPages: 0
            }
        }
    );

    React.useEffect(() => {
        saveFiltersToStorage({
            stateFilter,
            vendorSourceFilter,
            countriesFilter,
            vendorTypeFilter,
            categoryFilter
        });
    }, [
        stateFilter,
        vendorSourceFilter,
        countriesFilter,
        vendorTypeFilter,
        categoryFilter
    ]);

    React.useEffect(() => {
        vendors.request([
            searchParams,
            stateFilter,
            vendorSourceFilter,
            countriesFilter,
            vendorTypeFilter,
            categoryFilter
        ]);
    }, [searchParams]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [stateFilter]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [vendorSourceFilter]);
    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [countriesFilter]);
    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [vendorTypeFilter]);
    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [categoryFilter]);

    async function setState(item: VendorSupportDto, state: ProductState) {
        item.internalState = state;

        await walletClient.supportApi.vendorSupportControllerUpdateVendorState(
            item.id,
            {
                state: state
            }
        );
        dispatch({ type: 'reload' });
    }

    return (
        <>
            <PageHeader
                title="Vouchers catalog"
                actions={
                    <Button onClick={() => navigate(links.createVendor())}>
                        <Plus />
                        Create vendor
                    </Button>
                }
            />
            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<StateFilterType, false>
                    label="Status"
                    value={stateFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        {
                            label: 'enabled for users',
                            value: 'ENABLED_FOR_USER'
                        },
                        {
                            label: 'possible to enable',
                            value: 'POSSIBLE_TO_ENABLE'
                        }
                    ]}
                    onChange={(val) => void setStatusFilter(val)}
                />
                <Filters<VendorSourceFilterType, false>
                    label="Source"
                    value={vendorSourceFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        { label: 'Multivoucher', value: 'MULTIVOUCHER' },
                        { label: 'GoGift', value: 'GOGIFT' },
                        { label: 'Cadooz', value: 'CADOOZ' },
                        { label: 'Huuray', value: 'HUURAY' },
                        { label: 'Custom', value: 'CUSTOM' }
                    ]}
                    onChange={(val) => setVendorSourceFilter(val)}
                />

                <div className="w-[220px]">
                    <CountrySelect
                        label="Country"
                        value={countriesFilter}
                        onChange={(val) => setCountriesFilter(val)}
                    />
                </div>

                <Filters<VendorTypeFilterType, false>
                    label="Vendor Type"
                    value={vendorTypeFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        ...Object.values(VendorType).map((value) => ({
                            label: value,
                            value: value
                        }))
                    ]}
                    onChange={(val) => setVendorTypeFilter(val)}
                />

                <Filters<CategoryFilterType, false>
                    label="Category"
                    value={categoryFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        ...availableCategories.map((category) => ({
                            label: category.name.en || category.key,
                            value: String(category.id)
                        }))
                    ]}
                    onChange={(val) => setCategoryFilter(val)}
                />
            </div>
            <TableComponent<VendorSupportDto[]>
                emptyText="No entries"
                items={vendors}
                contextMenu={[
                    {
                        label: 'Go to Details',
                        action: (value) => {
                            navigate(links.vendorDetails(value.id));
                        }
                    },
                    {
                        label: 'Disable',
                        hidden: (value) =>
                            value.internalState == ProductState.DISABLED,
                        action: (value) =>
                            setState(value, ProductState.DISABLED)
                    },
                    {
                        label: 'Enable',
                        hidden: (value) =>
                            value.internalState == ProductState.PRODUCTION,
                        action: (value) =>
                            setState(value, ProductState.PRODUCTION)
                    }
                ]}
                columns={[
                    {
                        Header: '',
                        accessor: 'image',
                        style: { width: 112 },
                        Cell: ({
                            row: {
                                original: { image, id }
                            }
                        }: CellProps<VendorSupportDto>) => (
                            <Link to={links.vendorDetails(id)}>
                                {image && (
                                    <img
                                        className="rounded-md"
                                        src={getCdnImage(
                                            image.bucketName,
                                            image.objectKey,
                                            {
                                                resize: {
                                                    width: 112,
                                                    height: 64
                                                }
                                            }
                                        )}
                                    />
                                )}
                            </Link>
                        )
                    },
                    {
                        Header: 'Name',
                        accessor: 'name',
                        Cell: ({
                            row: {
                                original: { id, name }
                            }
                        }: CellProps<VendorSupportDto, string>) => (
                            <Link
                                to={links.vendorDetails(id)}
                                className="text-sm font-semibold"
                            >
                                {name}
                            </Link>
                        )
                    },
                    {
                        Header: 'Source',
                        accessor: (value) => value.source
                    },
                    {
                        Header: 'Countries',
                        accessor: (value) => value.countries.join(',')
                    },
                    {
                        Header: 'Categories',
                        accessor: (value) =>
                            value.categories
                                ?.map((c) => c.name.en || c.key)
                                .join(', ') || '',
                        Cell: ({
                            row: {
                                original: { categories }
                            }
                        }: CellProps<VendorSupportDto>) => (
                            <div className="flex flex-wrap gap-1">
                                {categories?.map((category) => (
                                    <Badge key={category.id} variant="outline">
                                        {category.name.en || category.key}
                                    </Badge>
                                ))}
                            </div>
                        )
                    },
                    {
                        Header: 'State',
                        Cell: ({
                            row: {
                                original: {
                                    internalState,
                                    externalState,
                                    shopState
                                }
                            }
                        }: CellProps<VendorSupportDto>) => (
                            <StateCell
                                internalState={internalState}
                                externalState={externalState}
                                shopState={shopState}
                            />
                        )
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                onSearchChange={(val) => void setSearchTerm(val)}
            />
        </>
    );
});

export default VendorList;
